import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDocumentMeta } from '../useDocumentMeta';
import { HEAD_BITCH_QUOTE, RELEASE_QUALIFIER } from '../siteConfig';
import {
  BOOKING_URL,
  EVENT_LINE,
  FORM_SOURCE,
  QR_SOURCES,
  TALLY_FORM_ID,
  VIDEO_POSTER,
  VIDEO_URL,
} from './config';
// The navy blooms (.hero-bg, .hero-dots) come from the homepage hero.
import '../HomePage.css';
import './TectonicsPage.css';

const TALLY_SCRIPT = 'https://tally.so/widgets/embed.js';

// Unfinished slots show a labelled box in development only. In a production
// build an unset video or form renders nothing.
const SHOW_PLACEHOLDERS = process.env.NODE_ENV === 'development';

declare global {
  interface Window {
    Tally?: { loadEmbeds: () => void };
  }
}

/* ---------- Video ---------- */

// Turns a normal YouTube or Vimeo share link into an embed URL that starts
// playing once loaded (it only loads after a tap) and plays inline on iOS.
function parseVideo(url: string): { embed: string; thumb: string } | null {
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/))([\w-]{11})/);
  if (yt) {
    return {
      embed: `https://www.youtube-nocookie.com/embed/${yt[1]}?autoplay=1&playsinline=1&rel=0`,
      thumb: `https://i.ytimg.com/vi/${yt[1]}/hqdefault.jpg`,
    };
  }
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/(\w+))?/);
  if (vimeo) {
    const hash = vimeo[2] ? `&h=${vimeo[2]}` : '';
    return {
      embed: `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1&playsinline=1${hash}`,
      thumb: '',
    };
  }
  return null;
}

const isMp4 = (url: string) => /\.mp4(?:[?#]|$)/i.test(url);

const DemoVideo: React.FC = () => {
  const [playing, setPlaying] = useState(false);

  // Self-hosted file: the browser's own player. Only metadata loads until
  // the visitor presses play, and it plays inline on iPhone.
  if (VIDEO_URL && isMp4(VIDEO_URL)) {
    return (
      <section className="tectonics-section">
        <div className="tectonics-video">
          <video controls playsInline preload="metadata" poster={VIDEO_POSTER || undefined}>
            <source src={VIDEO_URL} type="video/mp4" />
          </video>
        </div>
      </section>
    );
  }

  const video = VIDEO_URL ? parseVideo(VIDEO_URL) : null;

  if (!video) {
    if (!SHOW_PLACEHOLDERS) return null;
    return (
      <section className="tectonics-section">
        <div className="tectonics-video tectonics-placeholder">
          Demo video: set VIDEO_URL in src/tectonics/config.ts
        </div>
      </section>
    );
  }

  const poster = VIDEO_POSTER || video.thumb;
  return (
    <section className="tectonics-section">
      {playing ? (
        <div className="tectonics-video">
          <iframe
            src={video.embed}
            title="Cadence IO demo"
            allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
            allowFullScreen
          />
        </div>
      ) : (
        <button
          type="button"
          className="tectonics-video tectonics-video-poster"
          style={poster ? { backgroundImage: `url(${poster})` } : undefined}
          onClick={() => setPlaying(true)}
          aria-label="Play the demo video"
        >
          <span className="tectonics-play" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="28" height="28">
              <path d="M8 5.5v13l10.5-6.5z" fill="currentColor" />
            </svg>
          </span>
        </button>
      )}
    </section>
  );
};

/* ---------- Form ---------- */

// Tally posts a message to the parent window when the embedded form is
// submitted. Field shape differs a little between versions, so read both.
function firstNameFromSubmission(payload: any): string {
  const fields: any[] = Array.isArray(payload?.fields) ? payload.fields : [];
  const nameField = fields.find((f) => {
    const label = String(f?.title ?? f?.label ?? '');
    return /name/i.test(label) && !/company/i.test(label);
  });
  const value = nameField?.answer?.value ?? nameField?.value ?? '';
  return String(value).trim().split(/\s+/)[0] || '';
}

const DetailsForm: React.FC<{ qr: string; onSubmitted: () => void }> = ({ qr, onSubmitted }) => {
  const [submitted, setSubmitted] = useState(false);
  const [firstName, setFirstName] = useState('');
  const confirmationRef = useRef<HTMLParagraphElement>(null);

  // Load Tally's embed script after first paint, so the headline and form
  // slot never wait on it. It sizes the iframe to the form's height.
  useEffect(() => {
    if (!TALLY_FORM_ID) return;
    if (window.Tally) {
      window.Tally.loadEmbeds();
      return;
    }
    const existing = document.querySelector(`script[src="${TALLY_SCRIPT}"]`);
    if (existing) return;
    const script = document.createElement('script');
    script.src = TALLY_SCRIPT;
    script.async = true;
    script.onload = () => window.Tally?.loadEmbeds();
    // If the script can't load, fall back to a plain fixed-height iframe.
    script.onerror = () => {
      document.querySelectorAll<HTMLIFrameElement>('iframe[data-tally-src]').forEach((frame) => {
        if (!frame.src) frame.src = frame.dataset.tallySrc || '';
      });
    };
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== 'https://tally.so' || typeof event.data !== 'string') return;
      if (!event.data.includes('Tally.FormSubmitted')) return;
      try {
        const message = JSON.parse(event.data);
        if (message.event !== 'Tally.FormSubmitted') return;
        setFirstName(firstNameFromSubmission(message.payload));
        setSubmitted(true);
        onSubmitted();
      } catch {
        // Ignore anything that isn't a Tally message.
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onSubmitted]);

  useEffect(() => {
    if (submitted) confirmationRef.current?.focus();
  }, [submitted]);

  if (submitted) {
    return (
      <div className="tectonics-confirmation" aria-live="polite">
        <p ref={confirmationRef} tabIndex={-1} className="tectonics-confirmation-text">
          {firstName ? `Thanks, ${firstName}. I'll be in touch after the conference.` : "Thanks. I'll be in touch after the conference."}
        </p>
        <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="tectonics-text-link">
          Book a call →
        </a>
      </div>
    );
  }

  // Transparent background: the form sits directly on the navy page. Field,
  // text and button colours come from the form's theme in Tally.
  const params = new URLSearchParams({
    alignLeft: '1',
    hideTitle: '1',
    dynamicHeight: '1',
    transparentBackground: '1',
    source: FORM_SOURCE,
  });
  if (qr) params.set('qr', qr);
  const showForm = Boolean(TALLY_FORM_ID) || SHOW_PLACEHOLDERS;

  return (
    <>
      {showForm && <p className="tectonics-form-intro">Leave your details and we'll follow up after the conference.</p>}
      {TALLY_FORM_ID ? (
        <div className="tectonics-form">
          <iframe
            data-tally-src={`https://tally.so/embed/${TALLY_FORM_ID}?${params.toString()}`}
            loading="lazy"
            width="100%"
            height="620"
            title="Leave your details"
          />
        </div>
      ) : SHOW_PLACEHOLDERS && (
        <div className="tectonics-form tectonics-placeholder tectonics-form-placeholder">
          <span>Tally form: set TALLY_FORM_ID in src/tectonics/config.ts</span>
          <span>Hidden fields sent: source={FORM_SOURCE}{qr ? `, qr=${qr}` : ''}</span>
        </div>
      )}
    </>
  );
};

/* ---------- Page ---------- */

// This page opens the shared quote at "Cadence has become...". Derived from
// the homepage text so the rest of the quote can never drift from it.
const QUOTE_PREFIX = '"At Head Bitch Music, Cadence';
const QUOTE_TEXT = HEAD_BITCH_QUOTE.text.startsWith(QUOTE_PREFIX)
  ? '"Cadence' + HEAD_BITCH_QUOTE.text.slice(QUOTE_PREFIX.length)
  : HEAD_BITCH_QUOTE.text;

const TectonicsPage: React.FC = () => {
  useDocumentMeta(
    'Cadence IO: How many forms did your last release take?',
    'Enter release details once. Cadence IO delivers them to every platform in the format each one asks for.'
  );

  // Kept out of search. The static build copy of this page carries the same
  // tag in its HTML (scripts/tectonics-head.js) for crawlers that skip JS.
  useEffect(() => {
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex, nofollow';
    document.head.appendChild(robots);
    return () => robots.remove();
  }, []);

  const [searchParams] = useSearchParams();
  const s = (searchParams.get('s') || '').toLowerCase();
  const qr = QR_SOURCES.includes(s) ? s : '';

  const [submitted, setSubmitted] = useState(false);
  const onSubmitted = useCallback(() => setSubmitted(true), []);

  return (
    <main className="tectonics">
      <div className="tectonics-atmosphere" aria-hidden="true">
        <div className="hero-bg">
          <div className="hero-dots" />
        </div>
      </div>

      <section className="tectonics-hero">
        {EVENT_LINE && (
          <p className="tectonics-event">
            {/* Each part stays on one line; a phone wraps between them. */}
            {EVENT_LINE.split(' · ').map((part, i) => (
              <span key={part}>{i > 0 && ' · '}<span className="tectonics-event-part">{part}</span></span>
            ))}
          </p>
        )}
        <h1>How many forms did your last release take?</h1>
        <p className="tectonics-subhead">
          Enter release details once. Cadence IO delivers them to every platform in the format each one asks for.
        </p>
      </section>

      <div className="tectonics-column">
        <DemoVideo />

        <section className="tectonics-section">
          <DetailsForm qr={qr} onSubmitted={onSubmitted} />
        </section>

        <section className="tectonics-section">
          <p className="tectonics-qualifier">{RELEASE_QUALIFIER}</p>
          <blockquote className="tectonics-quote">
            <p>{QUOTE_TEXT}</p>
            <cite>{HEAD_BITCH_QUOTE.attribution}</cite>
          </blockquote>
        </section>

        <section className="tectonics-section">
          <p className="tectonics-founder">
            Founded by Sam Friske. Over 10 years in the music business, including Universal and Concord.
          </p>
        </section>

        {/* Hidden after a submit: the confirmation carries its own Book a call link. */}
        {!submitted && (
          <section className="tectonics-section">
            <p className="tectonics-ready">
              Ready to talk now?{' '}
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="tectonics-text-link">
                Book a call →
              </a>
            </p>
          </section>
        )}
      </div>
    </main>
  );
};

export default TectonicsPage;
